import { Component } from '@theme/component';
import { debounce, fetchConfig, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { positionTabIndicator } from '@theme/tabs';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/** Max viewport width (px) for swipe-down-to-close on search modal — matches CSS `max-width: 1023.98px`. */
const SEARCH_MODAL_SWIPE_MAX_WIDTH = 1023.98;
const SEARCH_MODAL_SWIPE_DISMISS_THRESHOLD = 0.25;
const SEARCH_MODAL_SWIPE_VELOCITY_THRESHOLD = 0.5;

/**
 * Transition used when releasing a swipe. Mirrors the custom sidebar's stylesheet
 * transition (`--transition-speed` / `--transition-easing`, i.e. 0.2s ease) rather
 * than the faster open/close keyframe speed, so the modal glides shut exactly like
 * the non-dialog sidebars.
 */
const SEARCH_MODAL_SWIPE_RELEASE_TRANSITION = 'transform var(--transition-speed) var(--transition-easing)';

/**
 * Added to the dialog surface while a swipe is dismissing it. It replaces the
 * surface's own `search-modal-slide-down-out` keyframe with a no-op keep-alive at
 * `--transition-speed` so it doesn't stack onto the inner component's glide (a
 * double translate), while `closeDialog`'s `onAnimationEnd` still resolves in step
 * with the glide and the backdrop fade.
 */
const SEARCH_MODAL_SWIPE_CLOSING_CLASS = 'search-modal__content--swipe-closing';

/**
 * Walks up from the touch target to the panel looking for a scrollable ancestor
 * that is currently scrolled away from the top. When found, a downward swipe should
 * scroll that content rather than dismiss the modal — so the drag is only armed when
 * this returns null (mirrors the non-dialog sidebar behavior).
 * @param {EventTarget | null} target
 * @param {HTMLElement} panel
 * @returns {HTMLElement | null}
 */
function getScrollableParent(target, panel) {
  let node = target instanceof HTMLElement ? target : null;
  while (node && node !== panel) {
    if (node.scrollHeight > node.clientHeight && node.scrollTop > 0) return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Cached markup for the "Recents" section, built once per modal session so repeated
   * searches don't refetch it. Reset to `null` when recently viewed products are cleared.
   * @type {string | null}
   */
  #recentsSectionHTML = null;

  /** @type {number} */
  #swipeTouchStartY = 0;
  /** @type {number} */
  #swipeTouchCurrentY = 0;
  /** @type {boolean} */
  #swipeIsDragging = false;
  /** @type {boolean} */
  #swipeIsCandidate = false;
  /** @type {number} */
  #swipeTouchStartTime = 0;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      const dialogSurface = this.closest('dialog');
      if (dialogSurface) {
        // Bind `touchstart` to the whole panel so a dismiss drag can begin anywhere
        // on it (like the non-dialog sidebars), except that a swipe starting inside a
        // scrolled scrollable region scrolls that content instead (see
        // `#onSearchModalSwipeTouchStart` / `getScrollableParent`). Track move/end on
        // the window so the gesture follows the finger off the panel.
        this.addEventListener('touchstart', this.#onSearchModalSwipeTouchStart, { signal, passive: true });
        window.addEventListener('touchmove', this.#onSearchModalSwipeTouchMove, { signal, passive: false });
        window.addEventListener('touchend', this.#onSearchModalSwipeTouchEnd, { signal });
        window.addEventListener('touchcancel', this.#onSearchModalSwipeTouchEnd, { signal });
      }
    }

    if (this.refs.searchTypes) {
      this.refs.searchTypes.addEventListener('click', this.#handleTypeFilter, { signal });
    }

    this.addEventListener('click', this.#handleBuyButton, { signal });

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles buy button clicks — adds item to cart, closes search, opens cart drawer.
   * @param {MouseEvent} event
   */
  #handleBuyButton = async (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('.resource-card__buy-button');
    if (!button) return;

    event.preventDefault();
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;

    try {
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const cartItemComponentsSectionIds = [];
      for (const item of cartItemsComponents) {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartItemComponentsSectionIds.push(item.dataset.sectionId);
        }
      }

      const payload = {
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: cartItemComponentsSectionIds.join(','),
      };

      const fetchResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await fetchResponse.json();

      if (data.status) {
        document.dispatchEvent(
          new CartErrorEvent(
            'predictive-search',
            String(data.message ?? ''),
            /** @type {object} */ (data.description ?? {}),
            /** @type {object} */ (data.errors ?? {})
          )
        );
        document.dispatchEvent(
          new CartAddEvent({}, 'predictive-search', {
            didError: true,
            source: 'product-form-component',
            itemCount: 0,
          })
        );
        return;
      }

      /** @type {{ item_count?: number } | null} */
      let cart = null;
      try {
        const cartResponse = await fetch('/cart.js');
        cart = await cartResponse.json();
      } catch (error) {
        console.error(error);
      }

      this.dialog?.closeDialog();
      document.dispatchEvent(
        new CartAddEvent(cart ?? undefined, 'predictive-search', {
          source: 'product-form-component',
          sections: data.sections,
          itemCount: cart?.item_count ?? 1,
          variantId,
        })
      );
    } finally {
      button.disabled = false;
    }
  };

  /**
   * Filters search results by type when a type button is clicked.
   * @param {MouseEvent} event
   */
  #handleTypeFilter = (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-filter]');
    if (!button) return;

    this.#setActiveFilter(button.dataset.filter);
  };

  #setActiveFilter(filter) {
    if (!this.refs.searchTypes) return;

    const buttons = this.refs.searchTypes.querySelectorAll('[data-filter]');
    for (const btn of buttons) {
      btn.classList.toggle('search__type-active', btn.dataset.filter === filter);
    }

    const sections = this.querySelectorAll('[data-search-type]');
    for (const section of sections) {
      const type = section.dataset.searchType;
      // "Recents" is exclusive to its own tab — it is never shown under "All Results".
      const visible = type === 'recents' ? filter === 'recents' : filter === 'all' || type === filter;
      section.hidden = !visible;
    }
  }

  /**
   * Reads the currently selected filter from the active tab button.
   * @returns {string} The active `data-filter` value, defaulting to `'all'`.
   */
  #getActiveFilter() {
    const active = this.refs.searchTypes?.querySelector('.search__type-active[data-filter]');
    return active instanceof HTMLElement && active.dataset.filter ? active.dataset.filter : 'all';
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearchModalSwipeTransform();
    this.#swipeIsDragging = false;
    this.#swipeIsCandidate = false;
    this.#resetSearch();
  };

  #isSearchModalSwipeViewport() {
    return window.matchMedia(`(max-width: ${SEARCH_MODAL_SWIPE_MAX_WIDTH}px)`).matches;
  }

  #resetSearchModalSwipeTransform() {
    this.closest('dialog')?.classList.remove(SEARCH_MODAL_SWIPE_CLOSING_CLASS);
    this.style.transition = '';
    this.style.transform = '';
  }

  #onSearchModalSwipeTouchStart = (/** @type {TouchEvent} */ event) => {
    this.#swipeIsDragging = false;
    this.#swipeIsCandidate = false;
    if (!this.dialog || !this.#isSearchModalSwipeViewport()) return;
    const surface = this.closest('dialog');
    if (!(surface instanceof HTMLElement) || !('open' in surface) || !surface.open) return;

    const touch = event.touches[0];
    if (!touch) return;

    // A swipe that begins inside a scrolled scrollable region (e.g. the results
    // list) must scroll that content, not dismiss the modal — so don't arm here.
    if (getScrollableParent(event.target, this)) return;

    this.#swipeTouchStartY = touch.clientY;
    this.#swipeTouchCurrentY = touch.clientY;
    this.#swipeTouchStartTime = Date.now();
    this.#swipeIsCandidate = true;
  };

  #onSearchModalSwipeTouchMove = (/** @type {TouchEvent} */ event) => {
    const touch = event.touches[0];
    if (!touch) return;

    // Wait for a downward move before committing, so an upward swipe (or a tap) is
    // left to the browser (e.g. scrolling a not-yet-scrolled results list upward).
    if (!this.#swipeIsDragging) {
      if (!this.#swipeIsCandidate || touch.clientY - this.#swipeTouchStartY <= 0) return;
      this.#swipeIsDragging = true;
      this.style.transition = 'none';
    }

    this.#swipeTouchCurrentY = touch.clientY;
    // Only translate the component downward — it slides toward the bottom to dismiss.
    const clampedDelta = Math.max(0, this.#swipeTouchCurrentY - this.#swipeTouchStartY);
    this.style.transform = `translateY(${clampedDelta}px)`;
    event.preventDefault();
  };

  #onSearchModalSwipeTouchEnd = () => {
    const wasDragging = this.#swipeIsDragging;
    this.#swipeIsDragging = false;
    this.#swipeIsCandidate = false;
    if (!this.dialog || !wasDragging) return;

    const deltaY = this.#swipeTouchCurrentY - this.#swipeTouchStartY;
    const elapsed = Date.now() - this.#swipeTouchStartTime;
    const velocity = elapsed > 0 ? deltaY / elapsed : 0;
    const height = this.offsetHeight || 1;
    const shouldDismiss =
      deltaY > height * SEARCH_MODAL_SWIPE_DISMISS_THRESHOLD ||
      velocity > SEARCH_MODAL_SWIPE_VELOCITY_THRESHOLD;

    // Continue the glide from the current drag position with the custom sidebar's
    // slower, eased transition instead of snapping the content back to 0 first.
    this.style.transition = SEARCH_MODAL_SWIPE_RELEASE_TRANSITION;

    if (shouldDismiss) {
      // Neutralize the dialog surface's own slide-down (it would stack onto this
      // glide as a double translate) and keep the dialog lifecycle + backdrop fade
      // in sync, then slide this component the rest of the way down.
      this.closest('dialog')?.classList.add(SEARCH_MODAL_SWIPE_CLOSING_CLASS);
      this.style.transform = 'translateY(100%)';
      void this.dialog.closeDialog();
    } else {
      // Snap back to the resting position.
      this.style.transform = 'translateY(0)';
    }
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();
    // Drop the cached markup and remove the tab — there is nothing left to show.
    this.#recentsSectionHTML = null;
    this.#toggleRecentsTab(false);

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#setActiveFilter('all');
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product,collection,article,page');
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    try {
      const resultsMarkup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
      if (!resultsMarkup) return;
      if (abortController.signal.aborted) return;

      const resultsRoot = new DOMParser()
        .parseFromString(resultsMarkup, 'text/html')
        .getElementById('predictive-search-results');

      // Carry recently viewed products into the results as their own exclusive
      // "Recents" section so the (query-only) Recents tab has something to show.
      const recentsSection = await this.#getRecentsSection();
      if (abortController.signal.aborted) return;

      if (resultsRoot && recentsSection) {
        resultsRoot.querySelector('.predictive-search-results__inner')?.prepend(recentsSection);
      }
      this.#toggleRecentsTab(Boolean(recentsSection));

      morph(predictiveSearchResults, resultsRoot ?? resultsMarkup);
      // Re-apply the active tab so the freshly-rendered sections honor it — notably
      // keeping the exclusive "Recents" section hidden under "All Results".
      this.#setActiveFilter(this.#getActiveFilter());
      this.#resetScrollPositions();
    } catch (error) {
      if (abortController.signal.aborted) return;
      throw error;
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  /**
   * Builds the exclusive, filterable "Recents" section for recently viewed products,
   * or returns `null` when there are none. The markup is fetched once and cached, and a
   * fresh element is returned on each call so it can be re-inserted after every morph.
   * @returns {Promise<HTMLElement | null>}
   */
  async #getRecentsSection() {
    if (RecentlyViewed.getProducts().length === 0) return null;

    if (this.#recentsSectionHTML == null) {
      const markup = await this.#getRecentlyViewedProductsMarkup();
      if (!markup) return null;

      const productsHtml = new DOMParser()
        .parseFromString(markup, 'text/html')
        .getElementById('predictive-search-products');
      if (!productsHtml) return null;

      for (const child of productsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const section = document.createElement('div');
      section.className = 'result-category-item predictive-search-results__recents';
      section.dataset.searchType = 'recents';
      section.append(...productsHtml.children);
      this.#recentsSectionHTML = section.outerHTML;
    }

    const template = document.createElement('template');
    template.innerHTML = this.#recentsSectionHTML;
    return /** @type {HTMLElement | null} */ (template.content.firstElementChild);
  }

  /**
   * Shows or hides the "Recents" tab button. The tab is only relevant once a query has
   * been made, so hiding it also falls back to the "all" filter when it was active.
   * @param {boolean} show - Whether the Recents tab should be visible.
   */
  #toggleRecentsTab(show) {
    const { searchTypes } = this.refs;
    const tab = searchTypes?.querySelector('[data-filter="recents"]');
    if (!(tab instanceof HTMLElement)) return;

    const wasActive = this.#getActiveFilter() === 'recents';
    tab.hidden = !show;

    if (!show && wasActive) {
      this.#setActiveFilter('all');
      // tabs.js only slides the indicator on a real tab click; this programmatic
      // switch needs an explicit reposition so it doesn't stay under the now-hidden
      // "Recents" tab.
      if (searchTypes) positionTabIndicator(searchTypes);
    }
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    if (abortController.signal.aborted) return;

    // Recently viewed lives behind the "Recents" tab, which only appears once a query
    // has been made — so the empty state renders neither the section nor the tab.
    this.#toggleRecentsTab(false);

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    // Re-apply the active tab so the freshly-rendered sections honor it — notably
    // keeping the exclusive "Recents" section hidden under "All Results".
    this.#setActiveFilter(this.#getActiveFilter());
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
